"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import { FormattedLocation } from "@/hooks/useLocationSearch";
import { budgetEstimatorEmail } from "@/lib/database/client/database";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Trash } from "iconoir-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { LocationAutocomplete } from "@/components/ui/create-event/LocationAutocomplete";

// Import the Select component from antd
import { createClient } from "@/utils/supabase/client";
import { Select } from "antd";

interface BudgetLineItem {
  id: string;
  name: string;
  category: string;
  estimatedCost: number;
  actualCost?: number;
  notes?: string;
}

interface EventDetails {
  numberOfNights: number;
  numberOfRooms: number;
  numberOfPeople: number;
  reasonForTravel: string[];
  destination: string;
  numberOfMeetingRooms: number;
  startingDestination: string[];
  endingDestination: string[];
  transportationType: "ground" | "flight";
}

interface CategoryData {
  category: string;
  total: number;
  percentage: number;
  color: string;
}

const TRAVEL_REASONS = ["Leisure", "Team Building", "Co-working", "Other"];

const CATEGORIES = ["accommodations", "food-beverages", "transportation", "activities", "misc-fees"];

const CATEGORY_LABELS = {
  accommodations: "Rooms",
  "food-beverages": "Food & Beverages",
  transportation: "Transportation",
  activities: "Activities",
  "misc-fees": "Other",
};

const CATEGORY_COLORS = {
  accommodations: "#3B82F6", // blue
  "food-beverages": "#10B981", // green
  transportation: "#F59E0B", // yellow
  activities: "#EF4444", // red
  "misc-fees": "#8B5CF6", // purple
};

// Default line item templates based on event details
const generateDefaultLineItems = (eventDetails: EventDetails): BudgetLineItem[] => {
  const items: BudgetLineItem[] = [];

  // Accommodations items
  if (eventDetails.numberOfRooms > 0) {
    items.push({
      id: "accommodations-1",
      name: "Accommodation cost",
      category: "accommodations",
      estimatedCost: eventDetails.numberOfRooms * eventDetails.numberOfNights * 200, // $200/night placeholder
      notes: `${eventDetails.numberOfRooms} rooms × ${eventDetails.numberOfNights} nights × $200/night`,
    });
  }

  if (eventDetails.numberOfMeetingRooms > 0) {
    items.push({
      id: "accommodations-2",
      name: "Meeting room cost",
      category: "accommodations",
      estimatedCost: eventDetails.numberOfMeetingRooms * 500 * eventDetails.numberOfNights, // $500/day placeholder
      notes: `${eventDetails.numberOfMeetingRooms} rooms × ${eventDetails.numberOfNights} nights × $500/room`,
    });
  }

  // Food & Beverages
  if (eventDetails.numberOfPeople > 0 && eventDetails.numberOfNights > 0) {
    items.push({
      id: "food-1",
      name: "Breakfast cost",
      category: "food-beverages",
      estimatedCost: eventDetails.numberOfPeople * eventDetails.numberOfNights * 25, // $25/person/day
      notes: `${eventDetails.numberOfPeople} people × ${eventDetails.numberOfNights} meals × $25/person`,
    });

    items.push({
      id: "food-2",
      name: "Lunch cost",
      category: "food-beverages",
      estimatedCost: eventDetails.numberOfPeople * eventDetails.numberOfNights * 30, // $30/person/day
      notes: `${eventDetails.numberOfPeople} people × ${eventDetails.numberOfNights} meals × $30/person`,
    });

    items.push({
      id: "food-3",
      name: "Dinner cost",
      category: "food-beverages",
      estimatedCost: eventDetails.numberOfPeople * eventDetails.numberOfNights * 40, // $40/person/day
      notes: `${eventDetails.numberOfPeople} people × ${eventDetails.numberOfNights} meals × $40/person`,
    });
  }

  // Transportation - Updated with ground vs flight pricing
  if (eventDetails.numberOfPeople > 0) {
    const transportationCost =
      eventDetails.transportationType === "flight"
        ? eventDetails.numberOfPeople * 750 // $750 per person for flight
        : eventDetails.numberOfPeople * 100; // $100 per person for ground

    const transportationName =
      eventDetails.transportationType === "flight" ? "Flight cost" : "Ground transportation cost";
    const transportationNotes =
      eventDetails.transportationType === "flight"
        ? `${eventDetails.numberOfPeople} people × $750/person (flight)`
        : `${eventDetails.numberOfPeople} people × $100/person (ground)`;

    items.push({
      id: "transport-1",
      name: transportationName,
      category: "transportation",
      estimatedCost: transportationCost,
      notes: transportationNotes,
    });
  }

  // Activities based on reason for travel
  if (eventDetails.numberOfPeople > 0 && eventDetails.numberOfNights > 0) {
    const reasons = eventDetails.reasonForTravel;
    const isLeisureOrTeamBuilding = reasons.includes("Leisure") || reasons.includes("Team Building");
    const isCoWorking = reasons.includes("Co-working");

    if (isLeisureOrTeamBuilding) {
      items.push({
        id: "activities-1",
        name: "Daily activities",
        category: "activities",
        estimatedCost: eventDetails.numberOfPeople * eventDetails.numberOfNights * 100, // $100/person/day
        notes: `${eventDetails.numberOfPeople} people × ${eventDetails.numberOfNights} days × $100/person/day`,
      });
    } else if (isCoWorking) {
      const activityDays = Math.ceil(eventDetails.numberOfNights / 2);
      items.push({
        id: "activities-1",
        name: "Activities (half days)",
        category: "activities",
        estimatedCost: eventDetails.numberOfPeople * activityDays * 50, // $50/person/day for half the days
        notes: `${eventDetails.numberOfPeople} people × ${activityDays} days × $50/person`,
      });
    }
  }

  return items;
};

// Pie Chart Component
const PieChart = ({ data, totalCost }: { data: CategoryData[]; totalCost: number }) => {
  const size = 200;
  const radius = size / 2;
  const center = size / 2;

  let currentAngle = 0;

  const createArc = (startAngle: number, endAngle: number, radius: number) => {
    const x1 = center + radius * Math.cos(startAngle);
    const y1 = center + radius * Math.sin(startAngle);
    const x2 = center + radius * Math.cos(endAngle);
    const y2 = center + radius * Math.sin(endAngle);

    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

    return `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  };

  return (
    <div className="flex flex-col items-center">
      <h3 className="text-tag-lg text-landing-primary font-semibold mb-4">Budget Breakdown</h3>
      <svg width={size} height={size} className="mb-4">
        {data.map((item, index) => {
          const startAngle = currentAngle;
          const endAngle = currentAngle + (item.percentage / 100) * 2 * Math.PI;
          currentAngle = endAngle;

          return (
            <path
              key={item.category}
              d={createArc(startAngle, endAngle, radius)}
              fill={item.color}
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
      </svg>

      <div className="space-y-2 w-full mb-4">
        {data.map((item) => (
          <div key={item.category} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
              <span className="text-landing-secondary">
                {CATEGORY_LABELS[item.category as keyof typeof CATEGORY_LABELS]}
              </span>
            </div>
            <span className="text-landing-primary font-medium">{item.percentage.toFixed(1)}%</span>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 pt-4 w-full">
        <div className="flex justify-between items-center">
          <span className="text-landing-primary font-semibold">Total</span>
          <span className="text-landing-primary font-bold text-lg">
            ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </div>
  );
};

interface BudgetEstimatorProps {
  createEventLink?: string;
  /** Override the flight estimator link (default: /flight-estimator) */
  flightEstimatorLink?: string;
  /** Hide the "Book a Demo" button (e.g. on enterprise pages) */
  hideBookDemo?: boolean;
}

export default function BudgetEstimator({
  createEventLink = "/create",
  flightEstimatorLink = "/flight-estimator",
  hideBookDemo = false,
}: BudgetEstimatorProps) {
  const { openModal, closeModal, isModalOpen } = useModalManager();
  const supabase = createClient();

  // Add authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  // Email collection state
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});

  const [eventDetails, setEventDetails] = useState<EventDetails>({
    numberOfNights: 0,
    numberOfRooms: 0,
    numberOfPeople: 0,
    reasonForTravel: [],
    destination: "",
    numberOfMeetingRooms: 0,
    startingDestination: [],
    endingDestination: [],
    transportationType: "flight",
  });

  const [startingDestinationsFormatted, setStartingDestinationsFormatted] = useState<FormattedLocation[]>([]);
  const [endingDestinationsFormatted, setEndingDestinationsFormatted] = useState<FormattedLocation[]>([]);

  const [items, setItems] = useState<BudgetLineItem[]>([]);
  const [newItem, setNewItem] = useState({ name: "", category: "lodging", estimatedCost: "", notes: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState({ name: "", category: "lodging", estimatedCost: "", notes: "" });
  const [originalEditValues, setOriginalEditValues] = useState({ name: "", estimatedCost: "", notes: "" });
  const [addingCategory, setAddingCategory] = useState<string | null>(null);
  const [showPerPerson, setShowPerPerson] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        setIsLoggedIn(!!user);
      } catch (error) {
        console.error("Error checking auth:", error);
        setIsLoggedIn(false);
      } finally {
        setIsLoadingAuth(false);
      }
    };

    checkAuth();
  }, [supabase]);

  const handleEventDetailsChange = (field: keyof EventDetails, value: any) => {
    const updatedDetails = { ...eventDetails, [field]: value };
    setEventDetails(updatedDetails);

    // Regenerate default line items when event details change
    const defaultItems = generateDefaultLineItems(updatedDetails);
    setItems(defaultItems);
  };

  // Helper function to handle number input changes
  const handleNumberChange = (field: keyof EventDetails, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value) || 0;
    handleEventDetailsChange(field, numValue);
  };

  const handleReasonToggle = (reason: string) => {
    const currentReasons = eventDetails.reasonForTravel;
    const updatedReasons = currentReasons.includes(reason)
      ? currentReasons.filter((r) => r !== reason)
      : [...currentReasons, reason];
    handleEventDetailsChange("reasonForTravel", updatedReasons);
  };

  const addCategoryItem = (category: string) => {
    setAddingCategory(category);
    setNewItem({
      name: "",
      category,
      estimatedCost: "",
      notes: "",
    });
  };

  const addItem = () => {
    if (!newItem.name) return;
    setItems([
      ...items,
      {
        id: Date.now().toString(),
        name: newItem.name,
        category: newItem.category,
        estimatedCost: Number(newItem.estimatedCost) || 0,
        notes: newItem.notes,
      },
    ]);
    setNewItem({ name: "", category: "lodging", estimatedCost: "", notes: "" });
    setAddingCategory(null);
  };

  const removeItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const startEdit = (item: BudgetLineItem) => {
    setEditingId(item.id);
    const editValues = {
      name: item.name,
      category: item.category,
      estimatedCost: item.estimatedCost.toString(),
      notes: item.notes || "",
    };
    setEditItem(editValues);
    setOriginalEditValues({
      name: item.name,
      estimatedCost: item.estimatedCost.toString(),
      notes: item.notes || "",
    });
  };

  const saveEdit = (id: string) => {
    const costChanged = editItem.estimatedCost !== originalEditValues.estimatedCost;
    const notesUnchanged = editItem.notes === originalEditValues.notes;

    // If cost changed but notes didn't change, set the notes to "Custom cost"
    const finalName = editItem.name; // Always keep the name
    const finalNotes = costChanged && notesUnchanged ? "Custom cost" : editItem.notes;

    setItems(
      items.map((item) =>
        item.id === id
          ? {
              ...item,
              name: finalName,
              category: editItem.category,
              estimatedCost: Number(editItem.estimatedCost) || 0,
              notes: finalNotes,
            }
          : item
      )
    );
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const cancelAdd = () => {
    setAddingCategory(null);
    setNewItem({ name: "", category: "lodging", estimatedCost: "", notes: "" });
  };

  const validateForm = () => {
    const errors: Record<string, boolean> = {};

    if (!firstName.trim()) errors.firstName = true;
    if (!lastName.trim()) errors.lastName = true;

    if (!email.trim()) {
      errors.email = true;
    } else if (!email.includes("@")) {
      errors.email = true;
    }

    if (!eventDetails.numberOfNights || eventDetails.numberOfNights < 1) {
      errors.numberOfNights = true;
    }

    if (!eventDetails.numberOfPeople || eventDetails.numberOfPeople < 1) {
      errors.numberOfPeople = true;
    }

    if (!eventDetails.reasonForTravel.length) {
      errors.reasonForTravel = true;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    // Validate form
    if (!validateForm()) {
      setSubmitError("Please fill in all required fields correctly.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare metadata with all form data
      const metadata = {
        firstName,
        lastName,
        eventDetails: {
          numberOfNights: eventDetails.numberOfNights,
          numberOfRooms: eventDetails.numberOfRooms,
          numberOfPeople: eventDetails.numberOfPeople,
          reasonForTravel: eventDetails.reasonForTravel,
          destination: eventDetails.destination,
          numberOfMeetingRooms: eventDetails.numberOfMeetingRooms,
          startingDestination: eventDetails.startingDestination,
          endingDestination: eventDetails.endingDestination,
        },
      };

      await budgetEstimatorEmail(supabase, email, metadata);
      setHasSubmittedEmail(true);
    } catch (error) {
      setSubmitError("Failed to submit. Please try again.");
      console.error("Error submitting email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalEstimated = items.reduce((sum, item) => sum + (item.estimatedCost || 0), 0);

  // Group items by category
  const groupedItems = items.reduce(
    (groups, item) => {
      const category = item.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
      return groups;
    },
    {} as Record<string, BudgetLineItem[]>
  );

  // Calculate per person values
  const perPersonMultiplier = showPerPerson && eventDetails.numberOfPeople > 0 ? 1 / eventDetails.numberOfPeople : 1;
  const displayTotal = totalEstimated * perPersonMultiplier;

  // Pie chart data
  const pieChartData: CategoryData[] = [];
  let totalPercentage = 0;
  for (const category in groupedItems) {
    const categoryItems = groupedItems[category];
    const categoryTotal = categoryItems.reduce((sum, item) => sum + (item.estimatedCost || 0), 0);
    if (categoryTotal > 0) {
      const percentage = (categoryTotal / totalEstimated) * 100;
      pieChartData.push({
        category,
        total: categoryTotal * perPersonMultiplier,
        percentage,
        color: CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] || "#000000", // Default color
      });
      totalPercentage += percentage;
    }
  }

  // Check if there are any non-zero items
  const hasNonZeroItems = items.some((item) => item.estimatedCost > 0);

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.5 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // Show email collection form only if not logged in and hasn't submitted email
  if (!isLoggedIn && !hasSubmittedEmail) {
    return (
      <div className="bg-landing-primary pb-24">
        {/* Hero Section */}
        <motion.section
          className="bg-landing-primary pt-32 pb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container-page">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-h1 font-bold text-landing-primary mb-4">Budget Estimator</h1>
              <p className="text-h4 text-landing-secondary mb-8">Smarter Budgeting for Smarter Events</p>
              <p className="text-body text-landing-secondary mb-6">
                Our new <strong>Budget Builder</strong> gives you full visibility into your event spend - before you
                make a single commitment. Plan with clarity. Decide with confidence.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Email Collection Form */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="container-page">
            <div className="max-w-4xl mx-auto">
              <motion.div
                className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="flex items-center justify-center gap-4">
                  <h2 className="text-h3 font-bold text-landing-primary mb-4 text-center">
                    Get Started with Your Budget
                  </h2>
                  <img src="/nowadays-money.png" alt="Public Nowadays Money" className="h-8 lg:h-12 w-auto mb-4" />
                </div>
                <p className="text-body text-landing-secondary mb-6 text-center">
                  Enter your details and basic event information to access our comprehensive budget estimator tool.
                </p>

                <form onSubmit={handleEmailSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <motion.div variants={fadeInUp} initial="initial" animate="animate">
                    <h3 className="text-h4 font-semibold text-landing-primary mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">First Name *</label>
                        <input
                          type="text"
                          required
                          className={`w-full border rounded-lg px-3 py-2 text-body focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.firstName ? "border-red-500" : "border-gray-300"
                          }`}
                          value={firstName}
                          onChange={(e) => {
                            setFirstName(e.target.value);
                            if (validationErrors.firstName) {
                              setValidationErrors((prev) => ({ ...prev, firstName: false }));
                            }
                          }}
                          placeholder="Enter your first name"
                        />
                        {validationErrors.firstName && (
                          <p className="text-red-500 text-sm mt-1">First name is required</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">Last Name *</label>
                        <input
                          type="text"
                          required
                          className={`w-full border rounded-lg px-3 py-2 text-body focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.lastName ? "border-red-500" : "border-gray-300"
                          }`}
                          value={lastName}
                          onChange={(e) => {
                            setLastName(e.target.value);
                            if (validationErrors.lastName) {
                              setValidationErrors((prev) => ({ ...prev, lastName: false }));
                            }
                          }}
                          placeholder="Enter your last name"
                        />
                        {validationErrors.lastName && (
                          <p className="text-red-500 text-sm mt-1">Last name is required</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">Email Address *</label>
                        <input
                          type="email"
                          required
                          className={`w-full border rounded-lg px-3 py-2 text-body focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.email ? "border-red-500" : "border-gray-300"
                          }`}
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (validationErrors.email) {
                              setValidationErrors((prev) => ({ ...prev, email: false }));
                            }
                          }}
                          placeholder="Enter your email address"
                        />
                        {validationErrors.email && (
                          <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {/* Event Details */}
                  <motion.div variants={fadeInUp} initial="initial" animate="animate" transition={{ delay: 0.1 }}>
                    <h3 className="text-h4 font-semibold text-landing-primary mb-4">Event Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">
                          Number of Nights *
                        </label>
                        <input
                          type="number"
                          required
                          className={`w-full border rounded-lg px-3 py-2 text-body focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.numberOfNights ? "border-red-500" : "border-gray-300"
                          }`}
                          value={eventDetails.numberOfNights || ""}
                          onChange={(e) => {
                            handleNumberChange("numberOfNights", e.target.value);
                            if (validationErrors.numberOfNights) {
                              setValidationErrors((prev) => ({ ...prev, numberOfNights: false }));
                            }
                          }}
                          min="1"
                          placeholder=""
                        />
                        {validationErrors.numberOfNights && (
                          <p className="text-red-500 text-sm mt-1">Number of nights is required (minimum 1)</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">Number of Rooms</label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-body focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={eventDetails.numberOfRooms || ""}
                          onChange={(e) => handleNumberChange("numberOfRooms", e.target.value)}
                          min="0"
                          placeholder=""
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">
                          Number of People *
                        </label>
                        <input
                          type="number"
                          required
                          className={`w-full border rounded-lg px-3 py-2 text-body focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            validationErrors.numberOfPeople ? "border-red-500" : "border-gray-300"
                          }`}
                          value={eventDetails.numberOfPeople || ""}
                          onChange={(e) => {
                            handleNumberChange("numberOfPeople", e.target.value);
                            if (validationErrors.numberOfPeople) {
                              setValidationErrors((prev) => ({ ...prev, numberOfPeople: false }));
                            }
                          }}
                          min="1"
                          placeholder=""
                        />
                        {validationErrors.numberOfPeople && (
                          <p className="text-red-500 text-sm mt-1">Number of people is required (minimum 1)</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">
                          Number of Meeting Rooms
                        </label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-body focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={eventDetails.numberOfMeetingRooms || ""}
                          onChange={(e) => handleNumberChange("numberOfMeetingRooms", e.target.value)}
                          min="0"
                          placeholder=""
                        />
                      </div>
                    </div>

                    {/* Fix the origin and destination section in the email collection form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">Origins</label>
                        <LocationAutocomplete
                          searchType="city"
                          selectedLocations={eventDetails.startingDestination}
                          onLocationChange={(selectedOptions, formattedLocations) => {
                            handleEventDetailsChange("startingDestination", selectedOptions);
                            setStartingDestinationsFormatted(formattedLocations);
                          }}
                          formattedLocations={startingDestinationsFormatted}
                          placeholder="Type and search for origins..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">Destinations</label>
                        <LocationAutocomplete
                          searchType="city"
                          selectedLocations={eventDetails.endingDestination}
                          onLocationChange={(selectedOptions, formattedLocations) => {
                            handleEventDetailsChange("endingDestination", selectedOptions);
                            setEndingDestinationsFormatted(formattedLocations);
                          }}
                          formattedLocations={endingDestinationsFormatted}
                          placeholder="Type and search for destinations..."
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">
                          Reason for Travel *
                        </label>
                        <Select
                          className="w-full"
                          placeholder="Select a reason"
                          value={eventDetails.reasonForTravel.length > 0 ? eventDetails.reasonForTravel[0] : undefined}
                          onChange={(selectedValue) => {
                            handleEventDetailsChange("reasonForTravel", selectedValue ? [selectedValue] : []);
                          }}
                          style={{
                            width: "100%",
                            borderRadius: "8px",
                          }}
                          allowClear
                          options={TRAVEL_REASONS.map((reason) => ({
                            label: reason,
                            value: reason,
                          }))}
                        />
                        {validationErrors.reasonForTravel && (
                          <p className="text-red-500 text-sm mt-1">Reason for travel is required</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-landing-primary mb-2">
                          Transportation Type
                        </label>
                        <Select
                          className="w-full"
                          placeholder="Select transportation type"
                          value={eventDetails.transportationType}
                          onChange={(selectedValue) => {
                            handleEventDetailsChange("transportationType", selectedValue || "flight");
                          }}
                          style={{
                            width: "100%",
                            borderRadius: "8px",
                          }}
                          options={[
                            { label: "Flight", value: "flight" },
                            { label: "Ground", value: "ground" },
                          ]}
                        />
                      </div>
                    </div>

                    {/* Flight Estimator Link */}
                    <div className="mt-4 text-center">
                      <p className="text-sm text-landing-secondary mb-2">Need accurate flight pricing?</p>
                      <a
                        href={flightEstimatorLink}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm underline"
                      >
                        Use our Flight Estimator →
                      </a>
                    </div>
                  </motion.div>

                  {/* Status Message */}
                  {submitError && (
                    <div className="p-3 rounded-md text-sm bg-red-50 text-red-700 border border-red-200">
                      {submitError}
                    </div>
                  )}

                  {/* Submit Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? "Submitting..." : "Access Budget Estimator"}
                    </Button>
                  </motion.div>
                </form>

                <p className="text-xs text-gray-500 mt-4 text-center">
                  By submitting this form, you agree to receive updates from Nowadays about our services and event
                  planning resources.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>
      </div>
    );
  }

  // Main budget estimator content with animations
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="budget-estimator"
        className="bg-landing-primary pb-24"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <motion.section
          className="bg-landing-primary pt-32 pb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container-page">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-h1 font-bold text-landing-primary mb-4">Budget Estimator</h1>
              <p className="text-h4 text-landing-secondary mb-8">Smarter Budgeting for Smarter Events</p>
              <p className="text-body text-landing-secondary mb-6">
                Our new <strong>Budget Builder</strong> gives you full visibility into your event spend - before you
                make a single commitment. Plan with clarity. Decide with confidence.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Main content */}
        <motion.div
          className="container-page"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
            {/* Left side - Event Details and Budget Table */}
            <div className="flex-1 flex flex-col gap-8">
              {/* Event Details Form */}
              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-tag-xl text-landing-primary">Event Details</h2>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-landing-secondary">Total Cost</span>
                    <button
                      onClick={() => setShowPerPerson(!showPerPerson)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        showPerPerson ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          showPerPerson ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                    <span className="text-sm text-landing-secondary">Per Person</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-landing-primary mb-2">Number of Nights</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-body"
                      value={eventDetails.numberOfNights || ""}
                      onChange={(e) => handleEventDetailsChange("numberOfNights", parseInt(e.target.value) || 0)}
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-landing-primary mb-2">Number of Rooms</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-body"
                      value={eventDetails.numberOfRooms || ""}
                      onChange={(e) => handleEventDetailsChange("numberOfRooms", parseInt(e.target.value) || 0)}
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-landing-primary mb-2">Number of People</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-body"
                      value={eventDetails.numberOfPeople || ""}
                      onChange={(e) => handleEventDetailsChange("numberOfPeople", parseInt(e.target.value) || 0)}
                      min="1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-landing-primary mb-3">Reason for Travel</label>
                    <Select
                      className="w-full"
                      placeholder="Select a reason"
                      value={eventDetails.reasonForTravel.length > 0 ? eventDetails.reasonForTravel[0] : undefined}
                      onChange={(selectedValue) => {
                        handleEventDetailsChange("reasonForTravel", selectedValue ? [selectedValue] : []);
                      }}
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                      }}
                      allowClear
                      options={TRAVEL_REASONS.map((reason) => ({
                        label: reason,
                        value: reason,
                      }))}
                    />
                    {validationErrors.reasonForTravel && (
                      <p className="text-red-500 text-sm mt-1">Reason for travel is required</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-landing-primary mb-3">Transportation Type</label>
                    <Select
                      className="w-full"
                      placeholder="Select transportation type"
                      value={eventDetails.transportationType}
                      onChange={(selectedValue) => {
                        handleEventDetailsChange("transportationType", selectedValue || "flight");
                      }}
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                      }}
                      options={[
                        { label: "Flight", value: "flight" },
                        { label: "Ground", value: "ground" },
                      ]}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-landing-primary mb-2">Origins</label>
                    <LocationAutocomplete
                      searchType="city"
                      selectedLocations={eventDetails.startingDestination}
                      onLocationChange={(selectedOptions, formattedLocations) => {
                        handleEventDetailsChange("startingDestination", selectedOptions);
                        setStartingDestinationsFormatted(formattedLocations);
                      }}
                      formattedLocations={startingDestinationsFormatted}
                      placeholder="Type and search for origins..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-landing-primary mb-2">Destinations</label>
                    <LocationAutocomplete
                      searchType="city"
                      selectedLocations={eventDetails.endingDestination}
                      onLocationChange={(selectedOptions, formattedLocations) => {
                        handleEventDetailsChange("endingDestination", selectedOptions);
                        setEndingDestinationsFormatted(formattedLocations);
                      }}
                      formattedLocations={endingDestinationsFormatted}
                      placeholder="Type and search for destinations..."
                    />
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-sm text-landing-secondary mb-2">Need accurate flight pricing?</p>
                  <a
                    href={flightEstimatorLink}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm underline"
                  >
                    Use our Flight Estimator →
                  </a>
                </div>
              </div>

              {/* Budget Card List - Only show when there are items */}
              {items.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <h2 className="text-tag-xl text-landing-primary">Budget Line Items</h2>
                      <img src="/nowadays-money.png" alt="Public Nowadays Money" className="h-8 lg:h-12 w-auto" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map((category) => (
                        <Button
                          key={category}
                          size="xs"
                          variant="secondary"
                          color="primary"
                          onClick={() => addCategoryItem(category)}
                        >
                          {`+ ${CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}`}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Add Item Form */}
                  {addingCategory && (
                    <div className="border border-blue-200 bg-blue-50 rounded-2xl px-6 py-4 flex flex-col gap-4">
                      <div className="text-sm font-medium text-blue-800">
                        Adding to: {CATEGORY_LABELS[addingCategory as keyof typeof CATEGORY_LABELS]}
                      </div>
                      <div className="flex flex-col gap-2 w-full">
                        <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                          <input
                            className="flex-1 min-w-[120px] border border-gray-300 rounded px-3 py-2 text-body"
                            placeholder="Line item name..."
                            value={newItem.name}
                            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                          />
                          <div className="relative w-full md:w-32">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              className="w-full border border-gray-300 rounded px-3 py-2 pl-6 text-body text-right"
                              placeholder="0.00"
                              value={newItem.estimatedCost}
                              onChange={(e) => setNewItem({ ...newItem, estimatedCost: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                          <input
                            className="flex-1 min-w-[120px] border border-gray-300 rounded px-3 py-2 text-body"
                            placeholder="Notes"
                            value={newItem.notes}
                            onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="xs"
                              variant="primary"
                              color="primary"
                              onClick={addItem}
                              leftIcon={<Plus className="w-4 h-4" />}
                            >
                              Add
                            </Button>
                            <Button size="xs" variant="secondary" color="neutral" onClick={cancelAdd}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    {CATEGORIES.map((category) => {
                      const categoryItems = groupedItems[category] || [];
                      const categoryTotal = categoryItems.reduce((sum, item) => sum + (item.estimatedCost || 0), 0);

                      // Only show category if it has items
                      if (categoryItems.length === 0) {
                        return null;
                      }

                      return (
                        <div key={category} className="space-y-2">
                          <h3 className="text-tag-lg text-landing-primary font-semibold px-2">
                            {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}
                          </h3>
                          {categoryItems.map((item) => (
                            <div
                              key={item.id}
                              className="border rounded-2xl px-6 py-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 w-full overflow-x-auto border-gray-200 bg-gray-50"
                            >
                              {editingId === item.id ? (
                                <div className="flex flex-col gap-2 w-full">
                                  <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                                    <input
                                      className="flex-1 min-w-[120px] border border-gray-300 rounded px-3 py-2 text-body"
                                      value={editItem.name}
                                      onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
                                      placeholder="Line Item Name"
                                    />
                                    <div className="relative w-full md:w-32">
                                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                        $
                                      </span>
                                      <input
                                        type="number"
                                        className="w-full border border-gray-300 rounded px-3 py-2 pl-6 text-body text-right"
                                        value={editItem.estimatedCost}
                                        onChange={(e) => setEditItem({ ...editItem, estimatedCost: e.target.value })}
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                                    <input
                                      className="flex-1 min-w-[120px] border border-gray-300 rounded px-3 py-2 text-body"
                                      value={editItem.notes}
                                      onChange={(e) => setEditItem({ ...editItem, notes: e.target.value })}
                                      placeholder="Notes"
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        size="xs"
                                        variant="primary"
                                        color="primary"
                                        onClick={() => saveEdit(item.id)}
                                      >
                                        Save
                                      </Button>
                                      <Button size="xs" variant="secondary" color="neutral" onClick={cancelEdit}>
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {/* Desktop Layout */}
                                  <div className="hidden md:flex flex-row gap-4 w-full items-center">
                                    <div className="flex-1 min-w-[120px] text-body font-medium text-landing-primary">
                                      {item.name}
                                    </div>
                                    <div className="w-32 text-right text-body flex-shrink-0">
                                      $
                                      {(item.estimatedCost * perPersonMultiplier).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </div>
                                    <div className="flex-1 min-w-[120px] text-body text-landing-secondary">
                                      {item.notes}
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                      <Button
                                        size="xs"
                                        variant="secondary"
                                        color="primary"
                                        onClick={() => startEdit(item)}
                                      >
                                        Edit
                                      </Button>
                                      <button
                                        onClick={() => removeItem(item.id)}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Mobile Layout */}
                                  <div className="flex md:hidden flex-col gap-2 w-full">
                                    <div className="flex justify-between items-center">
                                      <div className="text-body font-medium text-landing-primary text-left">
                                        {item.name}
                                      </div>
                                      <div className="text-body font-semibold text-landing-primary text-right">
                                        $
                                        {(item.estimatedCost * perPersonMultiplier).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <div className="text-body-sm text-landing-secondary text-left flex-1">
                                        {item.notes}
                                      </div>
                                      <div className="flex gap-2 flex-shrink-0">
                                        <Button
                                          size="xs"
                                          variant="secondary"
                                          color="primary"
                                          onClick={() => startEdit(item)}
                                        >
                                          Edit
                                        </Button>
                                        <button
                                          onClick={() => removeItem(item.id)}
                                          className="text-red-500 hover:text-red-700"
                                        >
                                          <Trash className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Summary */}
              {hasNonZeroItems && (
                <div className="bg-landing-primary shadow-[0_4px_100px_rgba(232,230,241,0.5)] border border-gray-200 p-6 rounded-3xl">
                  <h2 className="text-tag-xl text-landing-primary mb-4">Budget Summary</h2>
                  <div className="flex flex-col gap-2 text-body">
                    {/* Category subtotals */}
                    {CATEGORIES.map((category) => {
                      const categoryItems = groupedItems[category] || [];
                      const categoryTotal = categoryItems.reduce((sum, item) => sum + (item.estimatedCost || 0), 0);

                      if (categoryTotal > 0) {
                        return (
                          <div key={category} className="flex justify-between">
                            <span className="text-landing-secondary">
                              {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}
                            </span>
                            <span className="text-landing-primary font-semibold">
                              $
                              {(categoryTotal * perPersonMultiplier).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })}

                    {/* Nowadays sourcing fee */}
                    <div className="flex justify-between items-center">
                      <span className="text-landing-secondary text-left">Nowadays Sourcing Fee</span>
                      <span className="text-landing-primary font-semibold text-right">Contact us for pricing</span>
                    </div>

                    {/* Total */}
                    <div className="flex justify-between border-t border-gray-200 pt-2">
                      <span className="text-landing-secondary font-semibold">Total Estimated</span>
                      <span className="text-landing-primary font-bold text-lg">
                        $
                        {displayTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div className="mt-4 pt-4">
                      <div className="flex gap-3">
                        {!hideBookDemo && (
                          <Link href={createEventLink} className="w-1/2">
                            <Button variant="primary" size="lg" className="w-full">
                              Try our platform
                            </Button>
                          </Link>
                        )}
                        <Link href={createEventLink} className={hideBookDemo ? "w-full" : "w-1/2"}>
                          <Button
                            variant={hideBookDemo ? "primary" : "outline"}
                            color={hideBookDemo ? "primary" : "neutral"}
                            size="lg"
                            className="w-full"
                          >
                            Create an Event
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right side - Pie Chart */}
            {hasNonZeroItems && (
              <div className="lg:w-80 flex-shrink-0">
                <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm sticky top-8">
                  <PieChart data={pieChartData} totalCost={displayTotal} />
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Demo Modal */}
        <UnifiedModal
          type="cal"
          isOpen={isModalOpen("demo")}
          onClose={() => closeModal("demo")}
          title="Book a Demo"
          subtitle="Schedule a call to discuss your event planning needs"
          size="lg"
          calLink={DEMO_CALENDAR_LINK}
          fallbackText="Book your demo with our team"
        />
      </motion.div>
    </AnimatePresence>
  );
}
